import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUpdateDeposit } from '@/hooks/useUpdateDeposit';
import { Loader2, Shield, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';

interface DepositManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teacherId: string;
  teacherName: string;
  month: number;
  year: number;
  currentRefund: number;
  currentDeduction: number;
}

export const DepositManagementDialog = ({
  open,
  onOpenChange,
  teacherId,
  teacherName,
  month,
  year,
  currentRefund,
  currentDeduction,
}: DepositManagementDialogProps) => {
  const [depositRefund, setDepositRefund] = useState<string>('');
  const [depositDeduction, setDepositDeduction] = useState<string>('');
  
  const updateDeposit = useUpdateDeposit();

  useEffect(() => {
    if (open) {
      setDepositRefund(currentRefund?.toString() || '0');
      setDepositDeduction(currentDeduction?.toString() || '0');
    }
  }, [open, currentRefund, currentDeduction]);

  const handleSubmit = async () => {
    await updateDeposit.mutateAsync({
      teacherId,
      month,
      year,
      depositRefund: parseFloat(depositRefund) || 0,
      depositDeduction: parseFloat(depositDeduction) || 0,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            จัดการเงินประกัน
          </DialogTitle>
          <DialogDescription>
            {teacherName} - เดือน {month}/{year}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Deposit Refund */}
          <div className="space-y-2">
            <Label htmlFor="depositRefund" className="flex items-center gap-2">
              <ArrowUpCircle className="h-4 w-4 text-green-500" />
              คืนเงินประกัน (บาท)
            </Label>
            <Input
              id="depositRefund"
              type="number"
              min="0"
              step="0.01"
              value={depositRefund}
              onChange={(e) => setDepositRefund(e.target.value)}
              placeholder="0.00"
            />
          </div>

          {/* Deposit Deduction */}
          <div className="space-y-2">
            <Label htmlFor="depositDeduction" className="flex items-center gap-2">
              <ArrowDownCircle className="h-4 w-4 text-destructive" />
              หักเงินประกัน (บาท)
            </Label>
            <Input
              id="depositDeduction"
              type="number"
              min="0"
              step="0.01"
              value={depositDeduction}
              onChange={(e) => setDepositDeduction(e.target.value)}
              placeholder="0.00"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            ยกเลิก
          </Button>
          <Button onClick={handleSubmit} disabled={updateDeposit.isPending}>
            {updateDeposit.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            บันทึก
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
